import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { api, imageUrl, formatPrice, formatKm } from "../lib/api";
import { useCompare } from "../lib/compare";
import { ArrowLeft, X, Scale, Check, Minus, Send } from "lucide-react";

const SPEC_FIELDS = [
  { key: "year", label: "Anno" },
  { key: "km", label: "Km", format: (v) => (v || v === 0 ? formatKm(v) : "-") },
  { key: "fuel", label: "Alimentazione" },
  { key: "transmission", label: "Cambio" },
  { key: "body_type", label: "Carrozzeria" },
  { key: "color", label: "Colore" },
  { key: "power_hp", label: "Potenza (CV)" },
  { key: "power_kw", label: "Potenza (kW)" },
  { key: "engine_cc", label: "Cilindrata (cc)" },
];

export default function Compare() {
  const { ids, remove, clear } = useCompare();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const results = await Promise.all(
          ids.map((id) => api.get(`/cars/${id}`).then((r) => r.data).catch(() => null))
        );
        if (alive) setCars(results.filter(Boolean));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [ids]);

  const allOptionals = Array.from(
    new Set(cars.flatMap((c) => (Array.isArray(c.optionals) ? c.optionals : [])))
  ).sort();

  return (
    <div className="App min-h-screen flex flex-col" data-testid="compare-page">
      <Header />
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-10 md:py-14 w-full flex-1">
        <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
          <div>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-midnight/60 hover:text-gold mb-3"
              data-testid="compare-back-btn"
            >
              <ArrowLeft size={14} /> Torna al parco auto
            </Link>
            <div className="inline-flex items-center gap-3">
              <Scale size={22} className="text-gold" />
              <h1 className="font-display text-3xl md:text-4xl font-bold">Confronta Auto</h1>
            </div>
            <p className="text-midnight/60 mt-2">Metti a confronto fino a 3 vetture selezionate.</p>
          </div>
          {cars.length > 0 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const url = window.location.origin + "/confronta";
                  const lines = [
                    `⚖️ *Confronto Guzzardella Auto*`,
                    "",
                    ...cars.map((c) => `🚗 ${c.brand} ${c.model} (${c.year}) · ${formatKm(c.km)} · ${formatPrice(c.is_on_sale && c.sale_price ? c.sale_price : c.price)}`),
                    "",
                    `Confronta tutti i dettagli qui 👉 ${url}`,
                  ];
                  const text = encodeURIComponent(lines.join("\n"));
                  window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
                }}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white bg-[#25D366] hover:bg-[#1FB855] px-4 py-2 rounded-sm transition-colors font-semibold"
                data-testid="compare-share-whatsapp-btn"
                title="Invia il confronto via WhatsApp"
              >
                <Send size={13} />
                Invia via WhatsApp
              </button>
              <button
                onClick={clear}
                className="text-xs uppercase tracking-widest text-midnight/60 hover:text-destructive border border-border hover:border-destructive px-4 py-2 rounded-sm"
                data-testid="compare-clear-page-btn"
              >
                Svuota
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <p className="text-center text-midnight/50 py-16">Caricamento...</p>
        ) : cars.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-border rounded-sm">
            <Scale size={36} className="text-midnight/20 mx-auto mb-4" />
            <p className="text-midnight/60 mb-4">Nessuna auto in confronto.</p>
            <Link to="/" className="text-gold hover:underline text-sm uppercase tracking-widest">
              Sfoglia il parco auto
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-5 md:mx-0" data-testid="compare-table-wrapper">
            <table className="w-full min-w-[720px] border-collapse">
              <thead>
                <tr>
                  <th className="text-left p-3 w-40"></th>
                  {cars.map((c) => (
                    <th key={c.id} className="p-3 text-left align-top" style={{ width: `${100 / cars.length}%` }}>
                      <div className="card-sharp p-3 bg-white relative" data-testid={`compare-card-${c.id}`}>
                        <button
                          onClick={() => remove(c.id)}
                          className="absolute top-2 right-2 p-1.5 bg-midnight/80 text-white hover:bg-destructive rounded-sm"
                          aria-label="Rimuovi dal confronto"
                          data-testid={`compare-remove-${c.id}`}
                        >
                          <X size={12} />
                        </button>
                        {c.images?.[0] ? (
                          <div className="aspect-[4/3] bg-midnight overflow-hidden mb-3">
                            <img
                              src={imageUrl(c.images[0].storage_path)}
                              alt={`${c.brand} ${c.model}`}
                              className="w-full h-full object-contain"
                              loading="lazy"
                            />
                          </div>
                        ) : (
                          <div className="aspect-[4/3] bg-midnight-light flex items-center justify-center text-white/40 text-xs mb-3">
                            Nessuna foto
                          </div>
                        )}
                        <p className="text-[10px] uppercase tracking-widest text-midnight/50">{c.brand}</p>
                        <h3 className="font-display text-lg font-semibold text-midnight leading-tight truncate">{c.model}</h3>
                        <p className="font-display text-xl font-bold text-gold-outlined mt-2">
                          {formatPrice(c.is_on_sale && c.sale_price ? c.sale_price : c.price)}
                        </p>
                        <Link
                          to={`/auto/${c.id}`}
                          className="mt-3 block text-center text-[10px] uppercase tracking-widest text-gold hover:underline"
                          data-testid={`compare-view-${c.id}`}
                        >
                          Vedi scheda →
                        </Link>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-sm">
                {SPEC_FIELDS.map((f) => (
                  <tr key={f.key} className="border-t border-border">
                    <td className="p-3 text-xs uppercase tracking-widest text-midnight/50 font-semibold bg-muted/30">
                      {f.label}
                    </td>
                    {cars.map((c) => {
                      const v = c[f.key];
                      const display = f.format ? f.format(v) : (v || v === 0 ? String(v) : "-");
                      return (
                        <td key={c.id} className="p-3 text-midnight" data-testid={`compare-${f.key}-${c.id}`}>
                          {display}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {allOptionals.length > 0 && (
                  <tr className="border-t-2 border-gold/30 bg-gold/5">
                    <td colSpan={cars.length + 1} className="p-3 text-xs uppercase tracking-widest text-gold font-semibold">
                      Optional presenti
                    </td>
                  </tr>
                )}
                {allOptionals.map((opt) => (
                  <tr key={opt} className="border-t border-border">
                    <td className="p-3 text-xs text-midnight/70">{opt}</td>
                    {cars.map((c) => {
                      const yes = Array.isArray(c.optionals) && c.optionals.includes(opt);
                      return (
                        <td key={c.id} className="p-3" data-testid={`compare-opt-${opt}-${c.id}`}>
                          {yes ? (
                            <Check size={16} className="text-emerald-600" strokeWidth={2.5} />
                          ) : (
                            <Minus size={16} className="text-midnight/20" />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
